// WhyAustraliaPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyAustraliaPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'A System Built on Points',
      description: 'Skilled applicants are ranked against one another in SkillSelect, so you can see where you stand. Thousands of invitations are issued each year across a range of visa subclasses.'
    },
    {
      icon: Heart,
      title: 'Everyday Living Standards',
      description: 'Medicare covers your healthcare, schools and universities are strong, the weather is kind, and Australian cities appear near the top of global liveability rankings year after year.'
    },
    {
      icon: Briefcase,
      title: 'Real Demand for Skills',
      description: 'Wages sit high and unemployment low. Employers in IT, healthcare, engineering and several other sectors keep recruiting from overseas because they cannot fill roles locally.'
    },
    {
      icon: GraduationCap,
      title: 'Schooling for Your Children',
      description: 'Children of permanent residents attend public school without paying fees, and the universities rank well internationally. An Australian qualification travels well if you ever move on.'
    },
    {
      icon: Shield,
      title: 'What Residency Entitles You To',
      description: 'Permanent residents get Medicare cover, access to social security, and the same workplace and legal protections that apply to citizens.'
    },
    {
      icon: TrendingUp,
      title: 'Citizenship Further Down the Line',
      description: 'Australian citizenship opens up once you have 4 years of residence behind you, the last 12 months of which must be held as a permanent resident.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Why People Settle on Australia
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Australia runs one of the larger skilled migration programmes anywhere, and it pairs genuine career prospects with a standard of daily life that few countries match.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Worth knowing:</strong> unrestricted work rights, Medicare cover and the ability to sponsor eligible relatives all come with Australian permanent residency. The status itself carries no expiry date, provided you keep meeting the residency conditions attached to it.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyAustraliaPR;
